import { useSetAtom } from "jotai";
import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { logger } from "c7-logger";

import { useButtonDimensions } from "chameleon/lib/theme/helpers";
import { theme } from "chameleon/lib/theme/theme.css";

import { paymentSummaryAtom } from "checkout-experience/atoms/payment-atoms";
import {
  GoogleEnvironment,
  getGooglePayButtonOptions,
  getIsReadyToPayRequest,
  getPaymentDataRequest,
} from "checkout-experience/components/payment-method/pay-options/google/google-pay-payload";
import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { PaymentDomain } from "checkout-query-client/api/types";
import { ChexFieldValues } from "checkout-query-client/types/chex-form-fields";
import { PaymentType } from "checkout-query-client/types/form-types";
import { mapGoogleCardNetworkToProto } from "checkout-query-client/utils/build-payloads/build-google-payload";
import { getMerchantInfo } from "checkout-query-client/utils/google-pay/google-pay-payload";

interface GooglePayButtonProps {
  paymentDomain?: PaymentDomain;
  isDisabled: boolean;
}

export const GooglePayButton = ({ paymentDomain }: GooglePayButtonProps) => {
  const { i18n } = useTranslation(["combined"]);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const setPaymentSummary = useSetAtom(paymentSummaryAtom);
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { trigger, watch } = useFormContext();
  const { merchantSettings, checkoutSession, googlePayEnv, isDemo } =
    useChexContext();
  const { orderSummary } = checkoutSession ?? {};
  const googlePaySettings =
    merchantSettings?.paymentMethods?.walletPaymentMethod?.googlePay;

  const { height, padding } = useButtonDimensions(theme);

  const paymentClient = useMemo(() => {
    if (googlePaySettings) {
      return new google.payments.api.PaymentsClient({
        // For development now set to TEST
        environment: googlePayEnv as GoogleEnvironment,
        merchantInfo: getMerchantInfo({
          googlePaySettings,
        }),
      });
    }

    return null;
  }, [googlePaySettings]);

  useEffect(() => {
    if (!paymentClient || !merchantSettings || !googlePaySettings) return;

    paymentClient
      .isReadyToPay(
        getIsReadyToPayRequest({
          googlePaySettings,
          merchantSettings,
          googlePayEnv,
        })
      )
      .then(function (response) {
        if (response.result) {
          addGooglePayButton(paymentClient);
        }
      })
      .catch((err) => {
        logger.error("isReadyToPay error", err);
      });
  }, [paymentClient, merchantSettings, googlePayEnv]);

  if (!merchantSettings || !googlePaySettings || !orderSummary?.totalAmount)
    return null;

  const addGooglePayButton = (client: google.payments.api.PaymentsClient) => {
    const payButton = client.createButton({
      onClick: () => onGooglePaymentButtonClicked(client),
      ...getGooglePayButtonOptions({
        googlePaySettings,
        i18n,
        merchantSettings,
        googlePayEnv,
      }),
    });

    if (buttonRef?.current) {
      buttonRef.current.innerHTML = "";
      buttonRef.current.appendChild(payButton);
    }
  };

  const onGooglePaymentButtonClicked = (
    client: google.payments.api.PaymentsClient
  ) => {
    trigger(`${paymentOption?.formId}`)
      .then((isValid) => {
        if (isValid) {
          if (googlePaySettings.googlePayMerchantId === "DEMO" || isDemo) {
            paymentDomain?.({
              paymentType: PaymentType.googlePay,
              submitType: PaymentType.googlePay,
            }).catch(logger.error);
            return;
          }
          const paymentDataRequest = getPaymentDataRequest({
            orderSummary,
            googlePaySettings,
            merchantSettings,
          });

          client
            .loadPaymentData(paymentDataRequest)
            .then(function (paymentData) {
              processPayment(paymentData);
            })
            .catch((err: $TSNoType) => {
              logger.error("Google Payment Button click error: ", err);
            });
        }
      })
      .catch((err) => logger.error("Trigger Validation error: ", err));
  };

  const processPayment = (paymentData: google.payments.api.PaymentData) => {
    const formFields = watch(`${paymentOption?.formId}`) as ChexFieldValues;
    if (paymentDomain) {
      const cardData = paymentData.paymentMethodData.info;
      let paymentSummary;
      if (cardData) {
        paymentSummary = {
          paymentType: PaymentType.googlePay,
          last4: cardData.cardDetails,
          network: mapGoogleCardNetworkToProto(cardData.cardNetwork),
        };
        setPaymentSummary(paymentSummary);
      }

      paymentDomain({
        googlePaymentData: paymentData,
        paymentType: PaymentType.googlePay,
        submitType: PaymentType.googlePay,
        formFields: formFields,
        paymentSummary,
      }).catch(logger.error);
    }
  };

  return (
    <div>
      <style>
        {`
          #${PaymentType.googlePay} button {
            padding-top: ${padding}px;
            padding-bottom: ${padding}px;
            border-radius: ${theme.borderRadius.button};
          }
        `}
      </style>
      <div
        id={PaymentType.googlePay}
        style={{ height: height + padding * 2 }}
        ref={buttonRef}
      />
    </div>
  );
};
